import './PdcCard.scss'

import { HDSButton } from '@here/hds-react-components'
import CloseButton from 'components/App/components/CloseButton'
import List, { DividerProps } from 'components/App/components/List'
import ListItem from 'components/App/components/ListItem'
import { ScrollableContainer } from 'components/App/components/ScrollableContainer'
import { Status } from 'components/Collections/data/model'
import { addNotification } from 'components/Notifications/data/reducer'
import { TripAdvisorRating } from 'components/TripAdvisorRating'
import { RootState } from 'data/reducer'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import { i18n } from 'lib/i18n'
import { deserializePlaceV0 } from 'lib/place'
import { MediaEditorialsItem, MediaImagesItem, MediaRatingsItem } from 'map/services'
import React, { useCallback, useRef, useState } from 'react'
import { calculateAndFormatDistance, roundDecimalNumber } from 'utils/formatters'

import Accordion from './Accordion'
import PdcListItem from './PdcListItem'

interface PdcCardProps {
  item: PdcCardItem
  onCloseButtonClick: () => void
  onBookmarkButtonClick?: () => void
  onActionClick?: () => void
  onShare?: () => void
  actionLabel?: string
  onSuggestEditButtonClick?: () => void
  onFavorite?: ({ x, y }: { x: number; y: number }) => void
}

export interface PdcCardItem {
  id?: string
  title: string
  addressLabel?: string
  distanceLabel?: string
  averageRating?: number
  ratingLink?: string
  reviewCount?: number
  position?: {
    lat: number
    lng: number
  }
  primaryCategory?: {
    id: string
    name: string
    primary?: boolean
  }
  contacts?: {
    email?: string
    phone?: string
    www?: string
  }
  openingHours?: {
    status: string
    isOpen: boolean
    weekSchedule: {
      weekDay: string
      timings: string
    }[]
  }
  media?: {
    images?: {
      items: MediaImagesItem[]
    }
    ratings?: {
      items: MediaRatingsItem[]
    }
    editorials?: {
      items: MediaEditorialsItem[]
    }
  }
  resultType?: string
}

export default function PdcCard({
  item: pdcItem,
  onCloseButtonClick,
  onActionClick,
  onShare,
  actionLabel,
  onSuggestEditButtonClick,
  onFavorite,
}: PdcCardProps): JSX.Element {
  const dispatch = useAppDispatch()
  const favoriteButtonRef = useRef<Element>(null)
  const directionsLabel = actionLabel !== undefined ? actionLabel : i18n('pdc-card.directions')
  const suggestEditButtonLabel = i18n('pdc-card.suggest-edit')
  const seeMoreTripAdvisorLabel = i18n('pdc-card.trip-advisor')
  const [isOpeningHoursExpanded, setIsOpeningHoursExpanded] = useState<boolean>(false)
  const placeMaps = useAppSelector((state) => state.collections.placeMap)
  const collectionMaps = useAppSelector((state) => state.collections.collectionMap)
  const { user } = useAppSelector((state) => state.account)
  const countryCode = useAppSelector((state) => state.user.countryCode)
  const unit = useAppSelector((state) => state.app.unit)
  const isCollectionFetching = useAppSelector(
    ({ collections }) =>
      collections.status === Status.fetchCollectionsAndPlacesPending ||
      collections.status === Status.unknown
  )
  const isUserLoggedIn: boolean = useAppSelector(
    (state: RootState) => state.account.user !== undefined
  )

  const { coordinates: usersCurrentLocation } = useAppSelector(
    (state) => state.user.currentPosition
  )
  const positionString = pdcItem.position
    ? `${roundDecimalNumber(pdcItem.position.lat, 5)}, ${roundDecimalNumber(
        pdcItem.position.lng,
        5
      )}`
    : ''

  const handleOnSuggestEditButtonClick = useCallback(() => {
    if (onSuggestEditButtonClick !== undefined) onSuggestEditButtonClick()
  }, [onSuggestEditButtonClick])

  const handlePositionCopyClick = useCallback(() => {
    navigator.clipboard
      .writeText(positionString)
      .then(() => {
        dispatch(
          addNotification({
            content: i18n('notifications.clipboard-success'),
            duration: 5,
            inline: true,
            variant: 'confirmation',
          })
        )
      })
      .catch(console.error)
  }, [dispatch, positionString])

  const accordionStyle = {
    '--hds-icon-color': 'var(--hds-action)',
  } as React.CSSProperties
  const accordionLabelColor =
    pdcItem.openingHours && pdcItem.openingHours.isOpen
      ? 'var(--hds-positive)'
      : 'var(--hds-negative)'

  const handleOpeningHoursToggle = (toggle: boolean): void => {
    setIsOpeningHoursExpanded(toggle)
  }
  const dividerOptions: DividerProps = { position: { left: '1rem' } }

  const getCollectionName = (): string => {
    let collectionId = ''
    Object.values(placeMaps).forEach((place) => {
      const deserializedPlace = deserializePlaceV0(place.idV0)

      if (
        (place.placesForeignId === pdcItem.id || deserializedPlace.id === pdcItem.id) &&
        place.collectionId.length > 0
      ) {
        collectionId = place.collectionId[0]
      }
    })

    let collectionName = ''
    Object.values(collectionMaps).forEach((collection) => {
      if (collection.id === collectionId) {
        collectionName = collection.name
      }
    })
    return collectionName
  }

  const getCollectionCount = (): number => {
    let count = 0

    Object.values(placeMaps).forEach((place) => {
      const deserializedPlace = deserializePlaceV0(place.idV0)

      if (
        (place.placesForeignId === pdcItem.id || deserializedPlace.id === pdcItem.id) &&
        place.collectionId.length > 0
      ) {
        count = place.collectionId.length
      }
    })

    return count
  }

  const isPlaceInCollection = (): boolean => {
    return getCollectionCount() > 0
  }

  const collectionCount = getCollectionCount()

  return (
    <article className="PdcCard">
      <div className="PdcCard__title-wrapper">
        <header className="PdcCard__title">{pdcItem.title}</header>
        <CloseButton onClick={onCloseButtonClick} />
      </div>
      <section className="PdcCard__subtitle-wrapper">
        {pdcItem.position && usersCurrentLocation && (
          <span className="PdcCard__subtitle--highlighted" data-testid="PdcCard__distance-label">
            {calculateAndFormatDistance(pdcItem.position, usersCurrentLocation, {
              countryCode,
              unit,
            })}
          </span>
        )}

        {'addressLabel' in pdcItem && (
          <span className="PdcCard__subtitle">{pdcItem.addressLabel}</span>
        )}
      </section>

      {pdcItem.averageRating !== undefined && pdcItem.reviewCount !== undefined && (
        <TripAdvisorRating value={pdcItem.averageRating} reviewCount={pdcItem.reviewCount} />
      )}

      <section className="PdcCard__buttons-wrapper">
        <HDSButton
          size="medium"
          type="button"
          variant="primary"
          width="100%"
          onClick={onActionClick}
        >
          {directionsLabel}
        </HDSButton>

        <HDSButton
          iconCategory="discovery-sharing"
          icon="share-iOS"
          size="medium"
          type="button"
          variant="secondary"
          onClick={() => {
            onShare && onShare()
          }}
        />
        {pdcItem.id !== undefined && (
          <HDSButton
            ref={favoriteButtonRef}
            icon={isPlaceInCollection() ? 'star' : 'star-empty'}
            loading={isUserLoggedIn && isCollectionFetching}
            iconCategory="social"
            size="medium"
            type="button"
            variant="secondary"
            onClick={() => {
              if (favoriteButtonRef.current !== null) {
                const bounding = favoriteButtonRef.current.getBoundingClientRect()
                onFavorite && onFavorite({ x: bounding.x, y: bounding.y })
              }
            }}
          />
        )}
      </section>

      <ScrollableContainer>
        <>
          {pdcItem.media?.images?.items && pdcItem.media.images.items.length > 0 && (
            <img className="PdcCard__thumbnail" src={pdcItem.media.images.items[0].href} />
          )}

          <List divider={dividerOptions}>
            {collectionCount > 0 && (
              <PdcListItem
                type="label"
                icon="star"
                iconCategory="social"
                value={
                  collectionCount > 1
                    ? i18n('pdc-card.saved-in-multiple-collections')
                    : i18n('pdc-card.saved-in-collection', [getCollectionName()])
                }
              />
            )}
            {pdcItem.primaryCategory && (
              <PdcListItem
                type="category"
                icon={pdcItem.primaryCategory.id}
                value={pdcItem.primaryCategory.name}
              />
            )}

            {pdcItem.openingHours && (
              <>
                <ListItem className="PdcListItemWrapper__openingHours">
                  <Accordion
                    className="PdcListItemWrapper__accordion"
                    title={pdcItem.openingHours.status}
                    isExpanded={isOpeningHoursExpanded}
                    onToggle={handleOpeningHoursToggle}
                    icon="time"
                    style={accordionStyle}
                    labelColor={accordionLabelColor}
                  />
                </ListItem>
                {isOpeningHoursExpanded && (
                  <div className="PdcCard__opening-details-content">
                    {pdcItem.openingHours.weekSchedule.map(({ weekDay, timings }, index) => (
                      <div className="PdcCard__opening-details-label-wrapper" key={index}>
                        <div className="PdcCard__opening-details-weekday-label">{weekDay}</div>
                        <div className="PdcCard__opening-details-timings-label">{timings}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <PdcCardContacts pdcItem={pdcItem} />

            {pdcItem.position && (
              <PdcListItem
                type="position"
                icon="map-marker"
                iconCategory="map-view"
                value={positionString}
                onCopy={handlePositionCopyClick}
              />
            )}

            {pdcItem.ratingLink !== undefined && (
              <PdcListItem
                type="web"
                icon="external-link"
                value={pdcItem.ratingLink}
                label={seeMoreTripAdvisorLabel}
              />
            )}
          </List>
        </>
      </ScrollableContainer>

      {Boolean(user) && pdcItem.resultType === 'place' && (
        <>
          <footer className="PdcCard__footer">
            <HDSButton
              icon="edit"
              size="medium"
              type="button"
              variant="link"
              className="PdcCard__edit-button"
              onClick={handleOnSuggestEditButtonClick}
            >
              {suggestEditButtonLabel}
            </HDSButton>
          </footer>
        </>
      )}
    </article>
  )
}

function PdcCardContacts({ pdcItem }: { pdcItem: PdcCardItem }): JSX.Element | null {
  if (!pdcItem.contacts) {
    return null
  }

  return (
    <React.Fragment>
      {pdcItem.contacts.phone !== undefined && (
        <PdcListItem
          type="phone"
          icon="telephone"
          iconCategory="social"
          value={pdcItem.contacts.phone}
        />
      )}
      {pdcItem.contacts.www !== undefined && (
        <PdcListItem type="web" icon="global" iconCategory="misc" value={pdcItem.contacts.www} />
      )}
      {pdcItem.contacts.email !== undefined && (
        <PdcListItem
          type="email"
          icon="mail"
          iconCategory="social"
          value={pdcItem.contacts.email}
        />
      )}
    </React.Fragment>
  )
}
